import { Moment } from 'moment';
import type { IPlan, IMilestone } from '@/sections/management/accounts/orders/types';
import { IMilestoneSelected } from '@/components/data-display/billing-milestones/types';
import { IContactItem } from './entities/contacts';
import { ILegalEntityItem } from './legal-entity';
import { IOrganizationItem } from './organization';
import { IUserItem } from './user';
import { ISkuItem } from './sku';
import { IFormConsumptionOrderItemsMap } from '@/sections/management/accounts/orders/utils';
import { IOrderDocument } from './order-document';

export interface IOrderData {
  order?: IOrderItem;
  salesOrders?: ISalesOrder[];
  bagAllocations?: IConsumptionBagAllocation[];
  discounts?:
    | {
        amount: number;
        description: string;
      }[]
    | null;
}

export type IOrderItem = {
  _id: string;
  number: number;
  OVNumber: string; // Reference OV

  organization: IOrganizationItem;
  legalEntity: ILegalEntityItem;
  createdBy: IUserItem;
  submittedBy: IUserItem;
  contacts: IContactItem[];
  documents: any[];
  salesOrders?: ISalesOrder[];
  changeValidator?: boolean;
  previousRejectionReason: string;
  previouslyRejected: boolean;
  items: any[];
  itemsToRenewal: any[];
  itemsToUpgrade: any[];
  itemsToAddLicenses: any[];
  acceptedTermsAndConditions: boolean;
  isMarket: boolean;
  createdAt: string;
  submittedAt: string;
  status: string;
  billingStatus?: boolean;
  hasSyncError?: boolean;
  selectedMilestones?: IMilestoneSelected[];
  milestones?: IMilestone[];
  orderType?: string;
  discounts?:
    | {
        amount: number;
        description: string;
      }[]
    | null;
  approvalResponsibles?: IUserItem[];
  provisionResponsibles?: IUserItem[];
  totalInvoice?: number;
};

/**
 * Interface representing a consumption bag allocation with items and total amount
 */
export interface IConsumptionBagAllocation {
  consumptionOrderItems: IFormConsumptionOrderItemsMap;
  totalAmount: number;
  consumptionBagId: string;
}

/**
 * Interface representing the complete consumption bag allocations data
 */
export interface IConsumptionBagAllocationsData {
  orderId: string;
  bagAllocations: IConsumptionBagAllocation[];
}

export const ChoicesOrderItemLabel: { [key: string]: string } = {
  newResource: 'New Resource',
  upgrade: 'Upgrade',
  addLicenses: 'Add Licenses',
  renewal: 'Renewal',
  billingCloud: 'Billing Cloud',
};

export enum ChoicesStatusSalesOrder {
  active = 'active',
  inactive = 'inactive',
  pending_invoiced = 'pending_invoiced',
  invoiced = 'invoiced',
}

export type ISalesOrder = {
  _id: string;
  OVNumber: string;
  items?: any[];
  label?: string;
  legal_name?: string;
  tax_number?: string;
  createdAt: string;
  discounts: {
    amount: number;
    description: string;
  }[];
  documents: IOrderDocument[];
  legalEntity: {
    _id: string;
    legal_name: string;
    tax_number: string;
    erp_id?: string;
    activity?: string;
    city?: string;
    address?: string;
  };
  order: string;
  salesOrdersItem: any;
  status: ChoicesStatusSalesOrder;
  totalAmount: number;
  updateAt: string;
  salesOrdersItems?: ISaleOrderItem[];
};

export interface IBagAllocation {
  _id: string;
  consumptionBag: string;
  order: string;
  orderItem: string;
  allocatedValue: number;
  allocatedAmount: number;
  unitMeasure: string;
  isProcessed: boolean;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}
export interface ISaleOrderItem {
  _id: string;
  salesOrder?: string;
  orderItem: string;
  unitMeasure: string;
  value: number;
  amount: number;
  selectedBag?: any;
  createdAt?: Date;
  updatedAt?: Date;
}

export enum ChoicesOrderStatus {
  created = 'created',
  pendingApproval = 'pendingApproval',
  pendingProvision = 'pendingProvision',
  provisioning = 'provisioning',
  failedProvision = 'failedProvision',
  completed = 'completed',
  rejected = 'rejected',
  cancelled = 'cancelled',
}

export enum OrderTypes {
  PURCHASE_ORDER = 'purchase_order',
  BILLING_ORDER = 'billing_order',
}

export type IOrderTableFilters = {
  search: string;
  page: number;
  pageSize: number;
  organizations: any[];
  owners: any[];
  status: any[];
  sort: IOrderTable;
  excludeCreatedFromMarket?: boolean;
  isMarket: boolean;
  salesOrderNumber?: string;
  pendingBilling?: boolean;
  orderType?: OrderTypes;
};

type IOrderTable = {
  order: 'asc' | 'desc' | undefined;
  orderBy: string;
};

export type IOrderTableFiltersSubscription = {
  isMarket?: boolean;
  salesOrderNumber: string;
  search: string;
  status: [];
  createdBy: string;
  createdAt: string;
  ov: number;
  order: number;
  page: number;
  pageSize: number;
};

export enum ColumnsOrder {
  orderNumber = 'number',
  country = 'organization.country.name',
  company = 'organization.name',
  items = 'itemCount',
  documents = 'fileName',
  createdBy = 'createdBy.firstName',
  status = 'status',
  createdAt = 'createdAt',
  submittedBy = 'submittedBy.firstName',
  submittedAt = 'submittedAt',
}

export type IOrderTableFilterValue = string | string[] | any;

export enum ChoicesResourceType {
  RESOURCE_TYPE_UNSPECIFIED = 'RESOURCE_TYPE_UNSPECIFIED',
  SEAT = 'SEAT',
}

export type ChoicesOrderDisableFields = {
  add?: boolean;
  edit?: boolean;
  delete?: boolean;
  quantity?: boolean;
  company?: boolean;
};

export interface ICustomMilestone {
  id: string;
  paymentDate: Moment;
  amount: number;
  comment: string;
}

export interface IBillingOrderItem {
  _id: string;
  subscription: string;
  product: {
    name: string;
    label: string;
  };
  sku: ISkuItem;
  plan: IPlan;
  quantity: number;
  orderItemType: string;
}

export interface MilestoneData {
  _id: string;
  paymentDate: string;
  status: string;
  total: number;
  orderItem: IBillingOrderItem;
}

export interface IBillingOrderCurrentItem {
  _id: string;
  orderItem: IBillingOrderItem;
  product: IBillingOrderItem['product'];
  subscription: IBillingOrderItem['subscription'];
  plan: IPlan;
  quantity: number;
  totalQuantity: number;
  milestone: MilestoneData & { total: number };
  allMilestones: MilestoneData[];
}

export interface ISalesBillingOrderItem {
  value: number[];
  amount: number[];
  itemId: string[];
  subscription: string[];
  milestones: string[][];
}

export interface IBillingOrderItemRequest {
  _id: string;
  plan?: {
    sku?: {
      name?: string;
      product?: {
        label?: string;
        partner?: {
          name?: string;
          logo?: { url?: string };
        };
        name?: string;
      };
    };
    catalogName?: string;
    code?: string;
  };
  domain?: { domain?: string; label?: string } | null;
  summary?: {
    provision?: {
      milestones?: any[];
      quantity?: number;
    };
  };
  pre_sale?: {
    avatar?: { url?: string };
    firstName?: string;
    lastName?: string;
    partner?: { name?: string };
  } | null;
  orderItemType?: string;
  quantity?: number;
  selectedBag?: any;
  payFromBag?: boolean;
}
